import { collection, deleteDoc, doc, getDocs, setDoc } from 'firebase/firestore';
import { db } from './firebase';
import { MindMapNode, YouTubeVideoEntry } from '../types';
import { getVideosForNode } from './youtubeService';

// Videos whose note was deleted. Deleting a note removes its document — and with it the only
// record that linked the note's videos (still on YouTube, their backups still in Storage) to this
// app. Each such video is kept here so YouTube Manager can list it and attach it to another note.
export interface OrphanVideo {
    video: YouTubeVideoEntry;
    fromNodeId: string;
    fromNodeTitle: string;
    fromNotebookId: string;
    fromNotebookTitle: string;
    deletedAt: number;
}

const col = (uid: string) => collection(db, `users/${uid}/orphanVideos`);

/** Records the videos of notes that are being deleted. Best-effort: never blocks or fails the delete. */
export async function saveOrphanVideos(uid: string, notebook: { id: string; title?: string }, nodes: MindMapNode[]): Promise<number> {
    let saved = 0;
    for (const node of nodes) {
        for (const video of getVideosForNode(node)) {
            if (!video.youtubeVideoId) continue;
            const entry: OrphanVideo = {
                video: JSON.parse(JSON.stringify(video)), // Firestore rejects undefined fields
                fromNodeId: node.id,
                fromNodeTitle: node.title || '',
                fromNotebookId: notebook.id,
                fromNotebookTitle: notebook.title || '',
                deletedAt: Date.now(),
            };
            try { await setDoc(doc(col(uid), video.youtubeVideoId), entry); saved++; }
            catch (e) { console.error('[orphanVideos] could not record', video.youtubeVideoId, e); }
        }
    }
    return saved;
}

export async function listOrphanVideos(uid: string): Promise<OrphanVideo[]> {
    const snap = await getDocs(col(uid));
    return snap.docs.map(d => d.data() as OrphanVideo).filter(o => o?.video?.youtubeVideoId).sort((a, b) => b.deletedAt - a.deletedAt);
}

export async function removeOrphanVideo(uid: string, videoId: string): Promise<void> {
    await deleteDoc(doc(col(uid), videoId));
}

/** How many videos the given notes carry — for the delete confirmation. */
export const countVideos = (nodes: MindMapNode[]): number => nodes.reduce((n, node) => n + getVideosForNode(node).length, 0);
